import React, { useState, useCallback } from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useImpression } from '@thd-olt-component-react/impression';
import { ProjectsCard } from '../cards/ProjectsCard';
import { QuotesCard } from '../cards/QuotesCard';
import { UserListsCard } from '../cards/UserListsCard';
import { LatestOrdersCard } from '../cards/LatestOrdersCard';
import { ActiveOrdersCard } from '../cards/ActiveOrdersCard';
import { SnapshotTabBar } from './SnapshotTabBar';
import { TAB_META, ACTIVE_ORDERS_TAB_META, ACTIVE_ORDERS_FEATURE_SWITCH } from './constants';
import { triggerAnalyticsCustomEvent } from '../../utils/analyticsEvents';

const {
  string, node, shape, number
} = proptypes;

// The panel container, observed for the active tab's impression. useImpression only fires
// once its ref is attached to a real element (same pattern as ProAlertsAndUpdates' own
// section). Deliberately no ImpressionProvider: the cards' own impressions must keep the
// component-level ProAlertsAndUpdates provider as their container (AAP1-1153), not this one.
const TabImpression = ({ data, children }) => {
  const { ref, clickID } = useImpression({ data });

  return (
    // Floored at 29rem (a populated Orders tile); 1fr fills the snapshot's height (WOR2-1050).
    <div
      className="sui-grid sui-grow sui-min-w-0 sui-px-4 sm:sui-px-0 [&_.swiper-slide]:sui-w-auto"
      style={{ gridTemplateRows: 'minmax(29rem, 1fr)' }}
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      {children}
    </div>
  );
};

TabImpression.displayName = 'TabImpression';
TabImpression.propTypes = {
  data: shape({
    id: string,
    name: string,
    component: string,
    type: string,
    position: number,
  }).isRequired,
  children: node,
};

// Each tab's own carousel, named for what the customer actually sees (not the PAU wrapper
// card's displayName) so the DDO's impression name/component describe it.
const TAB_IMPRESSION_META = {
  projects: { name: 'ProjectsCarousel', component: 'ProjectsCarousel' },
  lists: { name: 'UserListsCarousel', component: 'UserListsCarousel' },
  quotes: { name: 'QuotesCarousel', component: 'QuotesCarousel' },
  latestOrders: { name: 'LatestOrdersCarousel', component: 'LatestOrdersCarousel' },
  activeOrders: { name: 'ActiveOrdersCarousel', component: 'ActiveOrdersCarousel' },
};

/**
 * Dashboard Snapshot tabs. Each tab's card owns its query, states and View All, and only the
 * active tab mounts. The orders tab is behind a feature switch (WOR2-1058).
 *
 * The panel fills the snapshot's height, so beside Loyalty it matches that row (WOR2-1050).
 */
export const SnapshotTabView = ({ componentId = '' }) => {
  const isActiveOrdersEnabled = useConfigService(ACTIVE_ORDERS_FEATURE_SWITCH) === true;
  const tabs = isActiveOrdersEnabled ? ACTIVE_ORDERS_TAB_META : TAB_META;

  const [activeKey, setActiveKey] = useState(tabs[0].key);

  // Falls back to the first tab if the switch changes the tab set while the orders tab is open.
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeKey));

  // Each tab fires its own card's existing listener instead of all four sharing Quotes'
  // (the WOR2-950 first pass) — Projects reuses projects.interaction's button-click case,
  // Latest Orders a new recentPurchases.tab-click case, and Lists a new, dedicated
  // lists.interaction-click listener (thd-analytics had no generic Lists listener yet).
  const handleTabClick = useCallback((_ev, index) => {
    const tab = tabs[index];

    switch (tab.key) {
    case 'projects':
      triggerAnalyticsCustomEvent('projects.interaction', {
        action: 'button-click',
        component: `${tab.label} tab`,
      });
      break;
    case 'lists':
      triggerAnalyticsCustomEvent('lists.interaction-click', {
        component: `${tab.label} tab`,
      });
      break;
    case 'latestOrders':
    case 'activeOrders':
      triggerAnalyticsCustomEvent('recentPurchases.tab-click');
      break;
    default:
      triggerAnalyticsCustomEvent('quotes.interaction-click', {
        component: `${tab.label} tab`,
      });
    }

    setActiveKey(tab.key);
  }, [tabs]);

  const activeTab = tabs[activeIndex];

  let panel;
  if (activeTab.key === 'projects') {
    panel = <ProjectsCard componentId={componentId} />;
  } else if (activeTab.key === 'lists') {
    panel = <UserListsCard componentId={componentId} />;
  } else if (activeTab.key === 'quotes') {
    panel = <QuotesCard componentId={componentId} />;
  } else if (activeTab.key === 'activeOrders') {
    panel = <ActiveOrdersCard />;
  } else {
    panel = <LatestOrdersCard componentId={componentId} />;
  }

  const { name: impressionName, component: impressionComponent } = TAB_IMPRESSION_META[activeTab.key];
  const tabImpression = {
    id: `${componentId}_${activeTab.key}`,
    name: impressionName,
    component: impressionComponent,
    type: 'content',
    position: 1,
  };

  return (
    // No gap from sm up: each tab's carousel adds its own 24px top inset there.
    <div className="sui-flex sui-flex-col sui-gap-4 sm:sui-gap-0 sui-w-full sui-grow">
      <SnapshotTabBar
        tabs={tabs}
        activeIndex={activeIndex}
        onTabClick={handleTabClick}
      />
      {/* One impression per tab ("<componentId>_<tab key>"), on top of each card's own state impression. */}
      <TabImpression key={activeTab.key} data={tabImpression}>
        {panel}
      </TabImpression>
    </div>
  );
};

SnapshotTabView.displayName = 'SnapshotTabView';
SnapshotTabView.propTypes = {
  componentId: string,
};
// Both orders cards are extended: dataModel is static, so it has to cover either switch state.
SnapshotTabView.dataModel = extend(ProjectsCard, QuotesCard, UserListsCard, LatestOrdersCard, ActiveOrdersCard);
